import {
  type TouchEvent,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import { useTradeOnCards } from "~/feature/event/components/MarketCalculator/components/TradeOn/useTradeOnCards";
import {
  useMarketCalculator,
  useMarketCalculatorActions,
} from "~/feature/event/providers/MarketCalculatorProvider";

const SWIPE_THRESHOLD_PX = 30;

export const useMarketCalculatorLineSlider = () => {
  const { market, groupedMarkets } = useMarketCalculator();
  const { setMarket, setGroupedMarkets } = useMarketCalculatorActions();
  const cardMarketIds = useTradeOnCards().map((card) => card.market.id);

  const cardMarketIdsRef = useRef<string[]>(cardMarketIds);
  cardMarketIdsRef.current = cardMarketIds;

  const freshAllMarkets = useMemo(
    () => (market ? [market, ...groupedMarkets] : []),
    [market, groupedMarkets]
  );

  const [sliderOrder, setSliderOrder] = useState<string[]>(cardMarketIds);
  const [activeIdx, setActiveIdx] = useState(0);
  const isSliderNavRef = useRef(false);

  useEffect(() => {
    if (isSliderNavRef.current) {
      isSliderNavRef.current = false;
      return;
    }
    if (market) {
      setSliderOrder(cardMarketIdsRef.current);
      setActiveIdx(0);
    }
  }, [market, market?.id]);

  const safeIdx = Math.min(activeIdx, Math.max(0, sliderOrder.length - 1));
  const activeMarket =
    freshAllMarkets.find((m) => m.id === sliderOrder[safeIdx]) ??
    freshAllMarkets[0] ??
    null;
  const hasMultiple = sliderOrder.length > 1;

  const navigateTo = useCallback(
    (newIdx: number) => {
      const newMarketId = sliderOrder[newIdx];
      const newMarket = freshAllMarkets.find((m) => m.id === newMarketId);
      if (!newMarket) return;
      isSliderNavRef.current = true;
      setActiveIdx(newIdx);
      setMarket(newMarket);
      setGroupedMarkets(freshAllMarkets.filter((m) => m.id !== newMarket.id));
    },
    [sliderOrder, freshAllMarkets, setMarket, setGroupedMarkets]
  );

  const goPrev = useCallback(
    () => navigateTo((safeIdx - 1 + sliderOrder.length) % sliderOrder.length),
    [navigateTo, safeIdx, sliderOrder.length]
  );
  const goNext = useCallback(
    () => navigateTo((safeIdx + 1) % sliderOrder.length),
    [navigateTo, safeIdx, sliderOrder.length]
  );

  const touchStartX = useRef<number | null>(null);

  const handleTouchStart = useCallback((e: TouchEvent) => {
    touchStartX.current = e.touches[0].clientX;
  }, []);

  const handleTouchEnd = useCallback(
    (e: TouchEvent) => {
      if (touchStartX.current === null) return;
      const delta = touchStartX.current - e.changedTouches[0].clientX;
      touchStartX.current = null;
      if (Math.abs(delta) < SWIPE_THRESHOLD_PX) return;
      if (delta > 0) {
        goNext();
      } else {
        goPrev();
      }
    },
    [goNext, goPrev]
  );

  return {
    activeMarket,
    hasMultiple,
    sliderOrder,
    safeIdx,
    goPrev,
    goNext,
    selectIndex: setActiveIdx,
    swipeHandlers: {
      onTouchStart: handleTouchStart,
      onTouchEnd: handleTouchEnd,
    },
  };
};
